import { h, Fragment } from 'preact'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { useState, useEffect } from 'preact/compat'
import GooglePayButton from '@google-pay/button-react'
import { PAYMENT_METHOD_TYPES } from '../../../constants/paymentMethods'
import ErrorMessage from '../../Common/ErrorMessage'
import { DataDog } from '../../../datadog'
import {
  dispatchMFEAdobeEvent,
  dispatchMFEEvent,
} from '../../../helpers/events'
import { useTranslations } from '../../../hooks/useTranslations'
import InfoMessage from '../../Common/InfoMessage'
import { formatAsCurrency, redirect, sprintf } from '../../../helpers/common'
import { authorizePayment } from '../../../api/googlePay'
import { SUCCESS_RESPONSES } from '../../../constants/serverResponses'
import { RESPONSE_ERROR_TEXT } from '../../../helpers/serverTextResponses'
import Loader from '../../Common/Loader'
import { pageStateSelector } from '../../../redux/slice/pageStateSlice'
import { transactionDetailsSelector } from '../../../redux/slice/transactionDetailsSlice'
import './style.scss'
import GooglePayAdapter from './GooglePayAdapter'

function GooglePay({ handleSubmit, showPaymentConfirmation, loaderImage }) {
  const l10n = useTranslations()
  const transactionDetails = useSelector(transactionDetailsSelector)
  const pageState = useSelector(pageStateSelector)
  const { isPaymentBlocked } = pageState
  const { culture, currency, overrideAmount, amount, successUrl, metadata } =
    transactionDetails

  const [error, setError] = useState('')
  const [isPaymentPending, setIsPaymentPending] = useState(false)
  const formatAmount = formatAsCurrency(currency, culture)

  useEffect(() => {
    if (!isPaymentBlocked) {
      setError('')
    }
  }, [isPaymentBlocked])

  // format number from 10000 to '100.00'
  const totalPrice = ((overrideAmount || amount) / 100).toFixed(2)
  GooglePayAdapter.updateTotalPrice(totalPrice)

  const handleClickGooglePay = e => {
    DataDog.info('Google Pay button was clicked')
    dispatchMFEEvent('tui-button.payment-btn-clicked', {
      paymentMethod: 'GooglePay',
    })
    dispatchMFEAdobeEvent('Pay-GooglePay')

    if (isPaymentBlocked) {
      setError(l10n.t('payment-btn-error'))
      e.preventDefault()
      return
    }
    setError('')
  }

  const handleOnLoadPaymentData = async payment => {
    DataDog.info(`Google Pay payment handleOnLoadPaymentData callback`)
    const continuePayment = () => {
      setIsPaymentPending(true)
      handleSubmit(async ({ notes }) => {
        const { responseStatus } = await authorizePayment(
          {
            amount,
            overrideAmount,
            notes,
            metadata,
          },
          payment,
        )

        if (responseStatus === SUCCESS_RESPONSES.OK) {
          window.tuiPaymentsMFEInstance?.onAuthorised?.()
          DataDog.info(
            `Redirection. Component: GooglePay, redirect url: ${successUrl}`,
          )
          redirect(successUrl)
        } else {
          setError(RESPONSE_ERROR_TEXT(l10n, responseStatus))
          setIsPaymentPending(false)
        }
      })()
    }

    const mfeElement = window.tuiPaymentsMFEInstance

    if (mfeElement?.onAuthorising) {
      DataDog.info(`onAuthorising hook has been enabled`)

      mfeElement
        .onAuthorising?.({ paymentMethod: PAYMENT_METHOD_TYPES.GooglePay })
        .then(async () => {
          DataDog.info(`onAuthorising Promise resolved`)
          await continuePayment()
        })
        .catch(() => {
          console.log('onAuthorising Promise rejected')
          DataDog.info(`onAuthorising Promise rejected`)
        })
    } else {
      await continuePayment()
    }
  }

  const handlePaymentError = reason => {
    DataDog.info(`Google Pay payment error, reason: `, reason)
    console.error('Google Pay error, reason: ', reason)
  }

  return (
    <>
      <ErrorMessage
        isShown={Boolean(error)}
        data-testid="googlepay-error"
        message={error}
      />

      {showPaymentConfirmation && (
        <InfoMessage
          message={sprintf(
            l10n.t(`You're about to pay %s with Google Pay`),
            formatAmount(overrideAmount || amount),
          )}
          data-testid="google-pay-info-message"
        />
      )}

      {isPaymentPending ? (
        <Loader loaderImage={loaderImage} />
      ) : (
        <div className="google-pay-container">
          <GooglePayButton
            className="google-pay-button"
            buttonType="plain"
            environment={GooglePayAdapter.enviroment}
            paymentRequest={GooglePayAdapter.getGooglePaymentDataRequest()}
            onClick={handleClickGooglePay}
            onLoadPaymentData={handleOnLoadPaymentData}
            onError={handlePaymentError}
          />
        </div>
      )}
    </>
  )
}

GooglePay.propTypes = {
  handleSubmit: PropTypes.func,
  showPaymentConfirmation: PropTypes.bool,
  loaderImage: PropTypes.string,
}

export default GooglePay
